/**
 * ArbitrageOpportunityCard Component
 * Eye-catching card displaying arbitrage opportunities between savings and debts
 * Design: Orange/red gradient background with big impact numbers
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

export interface ArbitrageOpportunity {
  type: string;
  from_type: string;
  from_amount: number;
  from_yield: number;
  to_type: string;
  to_amount: number;
  to_rate: number;
  transferable_amount: number;
  annual_gain: number;
  monthly_freed: number;
  safety_buffer: number;
  remaining_liquid_after: number;
  impact_description: string;
  roi_vs_subscription: number;
  payback_months: number | null;
}

interface ArbitrageOpportunityCardProps {
  opportunity: ArbitrageOpportunity;
  onPress?: () => void;
}

// Helper to format currency
const formatCurrency = (amount: number): string => {
  return amount.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + ' €';
};

// Helper to translate savings type
const translateSavingsType = (type: string): string => {
  const translations: Record<string, string> = {
    livret_a: 'Livret A',
    ldds: 'LDDS',
    pea: 'PEA',
    assurance_vie: 'Assurance Vie',
    compte_courant: 'Compte Courant',
    livret_jeune: 'Livret Jeune',
    pel: 'PEL',
    cel: 'CEL',
    autre: 'Autre épargne',
  };
  return translations[type] || type;
};

// Helper to translate debt type
const translateDebtType = (type: string): string => {
  const translations: Record<string, string> = {
    mortgage: 'Crédit immobilier',
    consumer: 'Crédit consommation',
    student: 'Prêt étudiant',
    auto: 'Crédit auto',
    credit_card: 'Carte de crédit',
    personal: 'Prêt personnel',
    other: 'Autre dette',
  };
  return translations[type] || type;
};

export default function ArbitrageOpportunityCard({
  opportunity,
  onPress,
}: ArbitrageOpportunityCardProps) {
  const spreadRate = opportunity.to_rate - opportunity.from_yield;

  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.9}>
      <View style={styles.container}>
        {/* Badge */}
        <View style={styles.badge}>
          <Text style={styles.badgeText}>OPPORTUNITE DETECTEE</Text>
        </View>

        {/* Main Value - Big Impact Number */}
        <View style={styles.mainValue}>
          <Text style={styles.plusSign}>+</Text>
          <Text style={styles.bigNumber}>{Math.round(opportunity.annual_gain)}</Text>
          <Text style={styles.currency}>€/an</Text>
        </View>

        {/* Subtitle */}
        <Text style={styles.subtitle}>
          Gain garanti en remboursant ta dette
        </Text>

        {/* Benefits List */}
        <View style={styles.benefitsList}>
          <View style={styles.benefitItem}>
            <Icon name="check-circle" size={18} color="#FFFFFF" />
            <Text style={styles.benefitText}>
              {formatCurrency(opportunity.transferable_amount)} à utiliser depuis ton {translateSavingsType(opportunity.from_type)}
            </Text>
          </View>

          <View style={styles.benefitItem}>
            <Icon name="check-circle" size={18} color="#FFFFFF" />
            <Text style={styles.benefitText}>
              {spreadRate.toFixed(1)} points d'intérêts économisés (placement garanti à {spreadRate.toFixed(1)}%)
            </Text>
          </View>

          {opportunity.monthly_freed > 0 && (
            <View style={styles.benefitItem}>
              <Icon name="check-circle" size={18} color="#FFFFFF" />
              <Text style={styles.benefitText}>
                +{formatCurrency(opportunity.monthly_freed)}/mois de cashflow libéré
              </Text>
            </View>
          )}

          <View style={styles.benefitItem}>
            <Icon name="shield-check" size={18} color="#FFFFFF" />
            <Text style={styles.benefitText}>
              Tu gardes {formatCurrency(opportunity.safety_buffer)} de matelas de sécurité
            </Text>
          </View>
        </View>

        {/* ROI Badge */}
        {opportunity.roi_vs_subscription > 0 && (
          <View style={styles.roiBadge}>
            <Icon name="trending-up" size={16} color="#FFD93D" />
            <Text style={styles.roiText}>
              {opportunity.payback_months && opportunity.payback_months < 12
                ? `Se paie en ${Math.ceil(opportunity.payback_months)} mois`
                : `ROI: ${opportunity.roi_vs_subscription.toFixed(1)}x l'abonnement`}
            </Text>
          </View>
        )}

        {/* CTA */}
        <View style={styles.ctaContainer}>
          <Text style={styles.ctaText}>Voir comment faire</Text>
          <Icon name="arrow-right" size={20} color="#FFFFFF" />
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    padding: spacing.lg,
    marginBottom: spacing.md,
    backgroundColor: '#E53935',
    // Shadow for depth
    shadowColor: '#FF3B30',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
    // CSS gradient for web
    ...(Platform.OS === 'web' && {
      // @ts-ignore - web only property
      backgroundImage: 'linear-gradient(135deg, #FF6B35 0%, #FF3B30 50%, #D62828 100%)',
    }),
  },

  // Badge
  badge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 20,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    alignSelf: 'flex-start',
    marginBottom: spacing.md,
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    letterSpacing: 1,
  },

  // Main Value
  mainValue: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: spacing.xs,
  },
  plusSign: {
    color: '#FFFFFF',
    fontSize: 36,
    fontWeight: typography.fontWeight.bold,
    marginRight: 2,
  },
  bigNumber: {
    color: '#FFFFFF',
    fontSize: 56,
    fontWeight: typography.fontWeight.bold,
    lineHeight: 60,
  },
  currency: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    marginLeft: spacing.xs,
    marginBottom: 8,
  },

  // Subtitle
  subtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.lg,
  },

  // Benefits
  benefitsList: {
    marginBottom: spacing.md,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  benefitText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    marginLeft: spacing.sm,
    flex: 1,
    lineHeight: 20,
  },

  // ROI Badge
  roiBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 20,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignSelf: 'flex-start',
    marginBottom: spacing.md,
  },
  roiText: {
    color: '#FFD93D',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    marginLeft: spacing.xs,
  },

  // CTA
  ctaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.sm,
  },
  ctaText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    marginRight: spacing.sm,
  },
});
